// @ts-strict-ignore
import { ContactEntityCompany, ContactEntityIndividual } from '@node-space/types'
import { isContactEntityCompany } from 'pages/Contacts/utils/utils'
import { Country } from 'types/authentication/Profile'
import {
  BeneficiaryDetailsCurrency,
  BeneficiaryTransferDestination,
  PaymentMethod,
  PaymentMethods,
} from 'types/beneficiaries'
import { Address, ContactInstrumentAccountHolderName, IntermediaryBank } from 'types/contacts'
import { getAddress } from 'utils/beneficiaries'
import { getCountryNameByCode } from 'utils/utils'

interface FormattedAddressResult {
  formatted: string | null
  country?: string
}

export const formatBankAddress = (
  bankAddress: Address,
  countries?: Country[]
): FormattedAddressResult => {
  if (!bankAddress) return { formatted: null }

  const { country: bankAddressCountry } = bankAddress
  const formatted = getAddress(
    {
      ...bankAddress,
      addressLine2: bankAddress?.addressLine2 || '',
      region: bankAddress?.region || '',
    },
    getCountryNameByCode(bankAddressCountry, countries)
  )

  return { formatted, country: bankAddressCountry }
}

export const formatAccountHolderAddress = (
  accountHolderAddress: Address,
  countries?: Country[]
): FormattedAddressResult => {
  if (!accountHolderAddress) return { formatted: null }

  const { country: accountHolderAddressCountry } = accountHolderAddress
  const formatted = getAddress(
    {
      ...accountHolderAddress,
      addressLine2: accountHolderAddress?.addressLine2 || '',
      region: accountHolderAddress?.region || '',
    },
    getCountryNameByCode(accountHolderAddressCountry, countries)
  )

  return { formatted, country: accountHolderAddressCountry }
}

export const getAccountHolderFullName = (
  accountHolderName?: ContactInstrumentAccountHolderName,
  contactEntity?: ContactEntityCompany | ContactEntityIndividual
): string | null => {
  if (!contactEntity) return null

  const contactEntityCompany = contactEntity as ContactEntityCompany
  const contactEntityIndividual = contactEntity as ContactEntityIndividual
  const isCompany = isContactEntityCompany(contactEntityCompany)
  const { legalName: contactLegalName } = contactEntityCompany || {}
  const { firstName: contactFirstName, lastName: contactLastName } = contactEntityIndividual || {}
  const {
    legalName: accountHolderLegalName,
    firstName: accountHolderFirstName,
    lastName: accountHolderLastName,
  } = accountHolderName || {}

  if (isCompany) {
    return accountHolderLegalName || contactLegalName || ''
  }

  return [accountHolderFirstName || contactFirstName, accountHolderLastName || contactLastName]
    .filter(Boolean)
    .join(' ')
}

export const getTransferDestination = (
  paymentMethods?: PaymentMethod[]
): BeneficiaryTransferDestination => {
  const isInternational = paymentMethods?.includes(PaymentMethods.SWIFT)
  return isInternational
    ? BeneficiaryTransferDestination.INTERNATIONAL
    : BeneficiaryTransferDestination.LOCAL
}

export const shouldShowSwiftCode = (
  currency: string,
  swiftCode?: string,
  isInternational?: boolean
): boolean => {
  if (!swiftCode) return false

  const isUsd = currency === BeneficiaryDetailsCurrency.USD
  return isUsd ? true : !!isInternational
}

export const shouldShowIdentificationCode = (
  currency: string,
  identificationCode?: string,
  isInternational?: boolean
): boolean => {
  if (!identificationCode) return false

  const isUsd = currency === BeneficiaryDetailsCurrency.USD
  return isUsd ? true : !isInternational
}

export const hasIntermediaryBankAddress = (intermediaryBankAddress?: Address): boolean => {
  if (!intermediaryBankAddress) return false

  const { addressLine1, city, country } = intermediaryBankAddress
  return !!(addressLine1 || city || country)
}

export const isIntermediaryBankDataPresent = (intermediaryBank?: IntermediaryBank): boolean => {
  if (!intermediaryBank) return false
  return !!(intermediaryBank.swiftCode || intermediaryBank.name)
}

export const formatIntermediaryBankAddress = (
  intermediaryBankAddress?: IntermediaryBank['address'],
  countries?: Country[]
): string => {
  if (!intermediaryBankAddress) return ''

  const { addressLine1, city, postCode, region, country } = intermediaryBankAddress

  return getAddress({
    addressLine1,
    city,
    postCode,
    region,
    country: getCountryNameByCode(country, countries),
  })
}
